import { useState, useRef, useEffect, useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { Mark, mergeAttributes } from "@tiptap/core";
import ClaudeService from "../services/claudeService";
import { fetchCreativeStarts } from "../services/creativeStartsService";
import { describeDeskReceipt } from "../lib/deskWrites";
import DeskVersionPreview from "./lumen/DeskVersionPreview";
import { useBookshelf } from "./BookshelfContext";
import { bookIdForDeskDoc } from "../lib/bookshelf";
import { copyInlineSelection } from "../lib/editorClipboard";

const TEAL = "#4DB8B5";

const START_OPTIONS = [
  { id: "outline",    icon: "📋", label: "Start with an outline",      desc: "Structure first, fill in later" },
  { id: "braindump",  icon: "🗣",  label: "Brain dump everything first", desc: "Get it all out, organize after" },
  { id: "conclusion", icon: "✅", label: "Begin with the conclusion",   desc: "Start at the end, work backwards" },
];

// ── Creative Starts scaffolds ──────────────────────────────────
// Each start option produces a genuinely different starting structure
// (Decision Surface Reduction Framework). Outline headers adapt to the
// likely content type inferred from the title.
function buildStartScaffold(startType, title = "") {
  const t = (title || "").toLowerCase();
  const isEmail   = /\b(email|e-mail|message|reply|reach out)\b/.test(t);
  const isPost    = /\b(blog|post|article|newsletter|thread)\b/.test(t);
  const isPlan    = /\b(plan|proposal|strategy|roadmap|project|pitch)\b/.test(t);
  const isMeeting = /\b(meeting|agenda|notes|standup|sync|1:1|one-on-one)\b/.test(t);
  const isEssay   = /\b(essay|paper|report|analysis|review)\b/.test(t);

  // Brain dump — minimal: one prompt line, then open space
  if (startType === "braindump")
    return "<blockquote>Get it all out first — every thought, fragment, half-idea. Don't organize, don't edit. You'll shape it later.</blockquote><p></p>";

  // Conclusion — summary up top, then work backward
  if (startType === "conclusion")
    return "<h2>Conclusion</h2><p>The main thing I want to land: </p>"
         + "<h2>Why it holds</h2><ul><li></li></ul>"
         + "<h2>Evidence &amp; support</h2><p></p>"
         + "<h2>Background — how we got here</h2><p></p>";

  // Outline — real scaffold, headers tuned to the likely content type
  if (isEmail)   return "<h2>Purpose</h2><p>What this email is about, in one line</p><h2>Context</h2><p></p><h2>The ask</h2><p></p><h2>Next steps</h2><p></p>";
  if (isPost)    return "<h2>Hook</h2><p>The idea that pulls them in</p><h2>The problem</h2><p></p><h2>Key points</h2><ul><li></li><li></li><li></li></ul><h2>Takeaway</h2><p></p>";
  if (isMeeting) return "<h2>Goal</h2><p></p><h2>Agenda</h2><ul><li></li><li></li></ul><h2>Discussion</h2><p></p><h2>Decisions</h2><p></p><h2>Action items</h2><ul><li></li></ul>";
  if (isPlan)    return "<h2>Objective</h2><p>What we're trying to achieve</p><h2>Background</h2><p></p><h2>Approach</h2><ul><li></li><li></li></ul><h2>Milestones</h2><p></p><h2>Risks &amp; open questions</h2><p></p>";
  if (isEssay)   return "<h2>Thesis</h2><p>The core argument in one sentence</p><h2>Introduction</h2><p></p><h2>Supporting point 1</h2><p></p><h2>Supporting point 2</h2><p></p><h2>Counterpoint</h2><p></p><h2>Conclusion</h2><p></p>";
  return "<h2>Overview</h2><p>What this is about</p><h2>Key point 1</h2><p></p><h2>Key point 2</h2><p></p><h2>Key point 3</h2><p></p><h2>Wrap-up</h2><p></p>";
}

// ── SidecarRevision TipTap Mark ────────────────────────────────
// Marks committed sidecar revisions with a teal underline
// Stores sessionId so clicking reopens the revision history
const SidecarRevisionMark = Mark.create({
  name: "sidecarRevision",
  addAttributes() {
    return {
      sessionId: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: "span[data-sidecar-session]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes, {
      "data-sidecar-session": HTMLAttributes.sessionId,
      style: "border-bottom: 2px solid #4DB8B5; cursor: pointer; transition: background 0.15s;",
    }), 0];
  },
});

// Finds the top-level block node(s) currently carrying a sidecarRevision mark for this
// sessionId, and returns the block-level range wrapping them (null if none exist yet).
// Positions stored from a prior edit go stale as soon as the doc changes elsewhere, so a
// re-commit must always re-locate its range live off the mark rather than reuse old ones.
function findSidecarMarkRange(doc, sessionId) {
  let from = null;
  let to = null;
  doc.forEach((node, offset) => {
    let hasMark = false;
    node.descendants(child => {
      if (child.isText && child.marks.some(m => m.type.name === "sidecarRevision" && m.attrs.sessionId === sessionId)) {
        hasMark = true;
      }
    });
    if (hasMark) {
      if (from === null) from = offset;
      to = offset + node.nodeSize;
    }
  });
  return from === null ? null : { from, to };
}

// ── Selection Popover ───────────────────────────────────────────
// Appears above selected text, triggers sidecar open
function SelectionPopover({ position, onOpen }) {
  if (!position) return null;
  return (
    <div className="fixed z-[200] bg-slate-800 text-white rounded-lg px-3 py-[5px] text-xs font-semibold font-['DM_Sans'] cursor-pointer shadow-[0_4px_16px_rgba(0,0,0,0.2)] flex items-center gap-1.5 whitespace-nowrap select-none -translate-x-1/2"
      style={{
        top: position.top - 40,
        left: position.left
      }}
      onMouseDown={e => { e.preventDefault(); onOpen(); }}
    >
      <span style={{ color: TEAL }}>✦</span> Refine in Sidecar
      {/* little arrow */}
      <div className="absolute -bottom-[5px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[5px] border-t-slate-800" />
    </div>
  );
}

// Strip leading H1 if it matches or approximates the title
function stripLeadingH1(html, title) {
  if (!html || !title) return html;
  // Remove any leading <h1>...</h1> tag from content
  return html.replace(/^\s*<h1[^>]*>.*?<\/h1>\s*/i, "").trim();
}

function extractTitle(html) {
  if (!html) return null;
  const m = html.match(/<h1[^>]*>(.*?)<\/h1>/i);
  return m ? m[1].replace(/<[^>]+>/g, "").trim() : null;
}

// ── Formatting Toolbar ──────────────────────────────────────────
function FormattingToolbar({ editor }) {
  if (!editor) return null;
  const btn = (action, label, active, title) => (
    <button onMouseDown={e => { e.preventDefault(); action(); }} title={title}
      className="px-2 py-1 rounded border-none cursor-pointer font-['DM_Sans'] min-w-[28px]"
      style={{
        background: active ? "rgba(77,184,181,0.15)" : "transparent",
        color: active ? TEAL : "#475569",
        fontSize: 13,
        fontWeight: active ? 700 : 500
      }}>
      {label}
    </button>
  );
  const div = () => <div className="w-px h-[18px] bg-black/10 mx-1 self-center" />;
  return (
    <div className="flex items-center gap-0.5 px-3 py-1.5 border-b border-black/[0.06] bg-white/80 flex-wrap flex-shrink-0">
      {btn(() => editor.chain().focus().toggleHeading({ level: 1 }).run(), "H1", editor.isActive("heading", { level: 1 }), "Heading 1")}
      {btn(() => editor.chain().focus().toggleHeading({ level: 2 }).run(), "H2", editor.isActive("heading", { level: 2 }), "Heading 2")}
      {btn(() => editor.chain().focus().toggleHeading({ level: 3 }).run(), "H3", editor.isActive("heading", { level: 3 }), "Heading 3")}
      {div()}
      {btn(() => editor.chain().focus().toggleBold().run(), "B", editor.isActive("bold"), "Bold")}
      {btn(() => editor.chain().focus().toggleItalic().run(), <em>I</em>, editor.isActive("italic"), "Italic")}
      {btn(() => editor.chain().focus().toggleStrike().run(), <s>S</s>, editor.isActive("strike"), "Strike")}
      {div()}
      {btn(() => editor.chain().focus().toggleBulletList().run(), "• List", editor.isActive("bulletList"), "Bullet list")}
      {btn(() => editor.chain().focus().toggleOrderedList().run(), "1. List", editor.isActive("orderedList"), "Numbered list")}
      {div()}
      {btn(() => editor.chain().focus().toggleBlockquote().run(), "❝", editor.isActive("blockquote"), "Blockquote")}
      {btn(() => editor.chain().focus().setHorizontalRule().run(), "—", false, "Divider")}
      {div()}
      {btn(() => editor.chain().focus().undo().run(), "↩", false, "Undo")}
      {btn(() => editor.chain().focus().redo().run(), "↪", false, "Redo")}
    </div>
  );
}

// ── Doc Decision Modal ─────────────────────────────────────────
function DocDecisionModal({ pendingAction, onNewDoc, onAppend, onReplace, onDismiss }) {
  if (!pendingAction) return null;
  const title = <em>{pendingAction.existingTitle || "current document"}</em>;
  const isVersion = pendingAction.kind === "version";
  return (
    <div className={`absolute bottom-20 left-1/2 -translate-x-1/2 bg-white border border-black/10 rounded-[14px] px-5 py-4 shadow-[0_8px_32px_rgba(0,0,0,0.12)] z-[100] font-['DM_Sans'] ${isVersion ? "w-[460px] max-w-[92%]" : "w-[380px]"}`}>
      <div className="text-[13px] font-semibold text-slate-800 mb-1.5">Where should this go?</div>
      <div className="text-xs text-slate-500 mb-3.5 leading-[1.5]">
        {isVersion
          ? <>Lumen wrote a new version of {title}. Update the doc, or keep both?</>
          : <>Add to {title}, or start a new document?</>}
      </div>
      {isVersion && (
        <div className="mb-3.5">
          <DeskVersionPreview currentHtml={pendingAction.currentContent} draftHtml={pendingAction.deskUpdate?.content} />
        </div>
      )}
      <div className="flex gap-2">
        {isVersion
          ? <button onClick={onReplace} className="flex-1 px-3 py-2 rounded-lg bg-accent-500/[0.08] border border-accent-500/25 text-accent-500 text-xs font-semibold cursor-pointer">Update doc</button>
          : <button onClick={onAppend} className="flex-1 px-3 py-2 rounded-lg bg-accent-500/[0.08] border border-accent-500/25 text-accent-500 text-xs font-semibold cursor-pointer">Add to existing</button>}
        <button onClick={onNewDoc} className="flex-1 px-3 py-2 rounded-lg border-none text-white text-xs font-semibold cursor-pointer" style={{ background: TEAL }}>{isVersion ? "Keep both" : "New document"}</button>
        <button onClick={onDismiss} className="px-2.5 py-2 rounded-lg bg-black/[0.04] border border-black/[0.08] text-slate-400 text-xs cursor-pointer">✕</button>
      </div>
    </div>
  );
}

// ── Sidecar Discard Confirm Modal ──────────────────────────────────────
function DiscardConfirmModal({ onDiscard, onCancel }) {
  return (
    <div className="absolute inset-0 bg-black/20 backdrop-blur-[2px] z-[120] flex items-center justify-center p-4" onMouseDown={onCancel}>
      <div className="bg-white border border-black/10 rounded-[14px] px-4 py-4 shadow-[0_8px_32px_rgba(0,0,0,0.18)] w-full max-w-[300px] font-['DM_Sans']" onMouseDown={e => e.stopPropagation()}>
        <div className="text-[13px] font-semibold text-slate-800 mb-1.5">Close without committing?</div>
        <div className="text-xs text-slate-500 mb-3.5 leading-[1.5]">
          Closing this will discard your changes. Commit to master first to keep them.
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="px-3 py-2 rounded-lg bg-black/[0.04] border border-black/[0.08] text-slate-500 text-xs font-semibold cursor-pointer">Cancel</button>
          <button onClick={onDiscard} className="px-3 py-2 rounded-lg border-none text-white text-xs font-semibold cursor-pointer" style={{ background: "#dc2626" }}>Discard</button>
        </div>
      </div>
    </div>
  );
}

// ── Sidecar ────────────────────────────────────────────────────
function SidecarPanel({ sessionId, selectedText, initialThread, initialLatest, onCommit, onClose }) {
  const [thread, setThread] = useState(
    initialThread || [{ role: "original", content: selectedText }]
  );
  const [latestRevision, setLatestRevision] = useState(initialLatest || null);
  const [loadedIndex, setLoadedIndex] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (initialLatest && initialThread) {
      const lastIdx = [...initialThread].map((m, i) => m.role === "assistant" ? i : -1).filter(i => i >= 0).pop();
      if (lastIdx !== undefined) setLoadedIndex(lastIdx);
    }
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread, loading]);

  const loadVersion = (idx, content) => {
    setLoadedIndex(idx);
    setLatestRevision(content);
  };

  const isDirty = Boolean(latestRevision && latestRevision !== initialLatest);
  const requestClose = () => {
    if (isDirty) setConfirmingClose(true);
    else onClose(thread, latestRevision);
  };

  const assistantIndices = thread.map((m, i) => m.role === "assistant" ? i : -1).filter(i => i >= 0);
  const activeAssistantIdx = loadedIndex ?? (assistantIndices[assistantIndices.length - 1] ?? null);

  const iterate = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    const updatedThread = [...thread, { role: "user", content: userMsg }];
    setThread(updatedThread);
    setLoading(true);
    try {
      const apiMessages = updatedThread
        .filter(m => m.role === "user" || m.role === "assistant")
        .map(m => ({ role: m.role === "assistant" ? "assistant" : "user", content: m.content }));
      const currentContent = latestRevision || selectedText;

      const result = await ClaudeService.sendMessage({
        model: "claude-sonnet-4-6",
        maxTokens: 1000,
        system: `You are Lumen, refining a piece of writing in Sidecar. The user selected this original text:\n\n"${selectedText}"\n\nThe current version is:\n\n"${currentContent}"\n\nRespond with ONLY the revised HTML. No explanation, no preamble, no markdown. Just the revised content ready to render.`,
        messages: apiMessages,
      });

      if (!result.success) {
        throw new Error(result.error);
      }

      const revised = result.content || "";
      const finalThread = [...updatedThread, { role: "assistant", content: revised }];
      const newIdx = finalThread.length - 1;
      setThread(finalThread);
      setLatestRevision(revised);
      setLoadedIndex(newIdx);
    } catch (error) {
      console.error("Sidecar error:", error);
      setThread(prev => [...prev, { role: "assistant", content: "<p>Something went wrong. Try again.</p>" }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="relative w-[340px] flex-shrink-0 border-l border-black/[0.08] flex flex-col bg-slate-50 font-['DM_Sans']">
      {/* Header */}
      <div className="px-4 py-3 border-b border-black/[0.06] flex items-center justify-between bg-white flex-shrink-0">
        <div>
          <div className="text-[13px] font-bold text-slate-800">✦ Sidecar</div>
          <div className="text-[10px] text-slate-400">Iterate without touching the master</div>
        </div>
        <button onClick={requestClose} className="bg-none border-none text-slate-400 cursor-pointer text-base">✕</button>
      </div>

      {/* Thread */}
      <div className="flex-1 overflow-auto px-3 py-3.5 flex flex-col gap-2.5">
        {thread.map((msg, i) => {
          if (msg.role === "original") {
            return (
              <div key={i}>
                <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.05em] mb-1.5">Selected text</div>
                <div className="p-3 rounded-[10px] bg-white border border-black/[0.08] text-[13px] text-slate-500 leading-[1.7]" dangerouslySetInnerHTML={{ __html: msg.content }} />
              </div>
            );
          }
          if (msg.role === "user") {
            return (
              <div key={i} className="self-end max-w-[80%] text-white rounded-xl rounded-br px-3 py-2 text-xs leading-[1.5]" style={{ background: TEAL }}>
                {msg.content}
              </div>
            );
          }
          if (msg.role === "assistant") {
            const isActive = i === activeAssistantIdx;
            const isOld = !isActive;
            return (
              <div key={i}>
                {isActive && (
                  <div className="text-[10px] font-semibold uppercase tracking-[0.05em] mb-1.5" style={{ color: TEAL }}>
                    {i === Math.max(...assistantIndices) ? "Latest version" : "Loaded version"}
                  </div>
                )}
                <div
                  onClick={() => isOld && loadVersion(i, msg.content)}
                  className="p-3 rounded-[10px] text-[13px] text-slate-700 leading-[1.7] transition-[opacity,border-color] duration-150"
                  style={{
                    background: isActive ? "rgba(77,184,181,0.04)" : "white",
                    border: isActive ? "1px solid rgba(77,184,181,0.25)" : "1px solid rgba(0,0,0,0.06)",
                    opacity: isActive ? 1 : 0.45,
                    cursor: isOld ? "pointer" : "default"
                  }}
                  onMouseEnter={e => { if (isOld) { e.currentTarget.style.opacity = "0.75"; e.currentTarget.style.borderColor = "rgba(77,184,181,0.2)"; }}}
                  onMouseLeave={e => { if (isOld) { e.currentTarget.style.opacity = "0.45"; e.currentTarget.style.borderColor = "rgba(0,0,0,0.06)"; }}}
                  dangerouslySetInnerHTML={{ __html: msg.content }}
                />
                {isOld && (
                  <div className="text-[10px] text-slate-400 text-right mt-[3px] font-['DM_Sans'] cursor-pointer"
                    onClick={() => loadVersion(i, msg.content)}>
                    ↑ load this version
                  </div>
                )}
              </div>
            );
          }
          return null;
        })}
        {loading && <div className="self-start bg-slate-100 rounded-xl rounded-bl px-3.5 py-2 text-base tracking-[2px]" style={{ color: TEAL }}>···</div>}
        <div ref={endRef} />
      </div>

      {/* Input + Commit */}
      <div className="px-3 py-2.5 border-t border-black/[0.06] flex flex-col gap-2 bg-white flex-shrink-0">
        <div className="flex gap-1.5">
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && iterate()} placeholder="Make it more concise..." className="flex-1 px-2.5 py-2 rounded-lg border border-black/10 bg-slate-50 text-slate-800 text-xs font-['DM_Sans'] outline-none" />
          <button onClick={iterate} disabled={loading || !input.trim()} className="px-3 py-2 rounded-lg border-none text-white text-sm font-semibold" style={{ background: loading || !input.trim() ? "rgba(77,184,181,0.3)" : TEAL, cursor: loading || !input.trim() ? "default" : "pointer" }}>→</button>
        </div>
        <button onClick={() => onCommit(latestRevision, thread)} disabled={!latestRevision} className="px-2 py-2 rounded-lg text-xs font-semibold font-['DM_Sans']" style={{ background: latestRevision ? "rgba(77,184,181,0.08)" : "rgba(0,0,0,0.03)", border: latestRevision ? "1px solid rgba(77,184,181,0.25)" : "1px solid rgba(0,0,0,0.06)", color: latestRevision ? TEAL : "#CBD5E1", cursor: latestRevision ? "pointer" : "default" }}>← Commit to Master</button>
        {!latestRevision && <div className="text-[10px] text-slate-300 text-center">Ask Lumen to refine first</div>}
      </div>

      {confirmingClose && (
        <DiscardConfirmModal
          onDiscard={() => { setConfirmingClose(false); onClose(thread, latestRevision); }}
          onCancel={() => setConfirmingClose(false)}
        />
      )}
    </div>
  );
}

// ── Creative Starts ────────────────────────────────────────────
// Fallback options used when the Lumen call fails or times out.
// Scaffold is generated from the title so the fallback is at least title-aware.
function getFallbackOptions(title) {
  return START_OPTIONS.map(opt => ({
    ...opt,
    scaffold: buildStartScaffold(opt.id, title),
  }));
}

// CreativeStartsModal — fetches contextual options from Lumen on mount.
// Shows a brief loading state, then displays the three options.
// Falls back to hardcoded START_OPTIONS if the call fails or times out.
function CreativeStartsModal({ title, roomCtx, existingDocTitles, onChoose, onClose }) {
  const [options, setOptions] = useState(null); // null = still loading

  useEffect(() => {
    let cancelled = false;
    fetchCreativeStarts(title, roomCtx, existingDocTitles).then(result => {
      if (cancelled) return;
      setOptions(result || getFallbackOptions(title));
    });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loading = options === null;

  return (
    <div className="absolute inset-0 bg-white/[0.97] backdrop-blur-lg z-50 flex flex-col items-center justify-center gap-5 p-8 rounded-2xl">
      <div className="text-center">
        <div className="text-base font-bold text-slate-800 font-['DM_Sans'] mb-1.5">How would you like to start?</div>
        <div className="text-xs text-slate-400 font-['DM_Sans']">{title || "New document"}</div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center gap-2 py-6">
          <div className="text-[11px] text-slate-400 font-['DM_Sans']">Thinking...</div>
        </div>
      ) : (
        <div className="flex flex-col gap-2.5 w-full max-w-[400px]">
          {options.map((opt, i) => (
            <button key={i} onClick={() => onChoose(opt.scaffold)}
              className="px-4 py-3.5 rounded-xl bg-white border border-black/10 flex items-center gap-3 cursor-pointer text-left shadow-[0_1px_4px_rgba(0,0,0,0.06)] transition-[border-color] duration-150"
              style={{ borderColor: "rgba(0,0,0,0.1)" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = TEAL; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(0,0,0,0.1)"; }}>
              <span className="text-[22px]">{opt.icon}</span>
              <div>
                <div className="text-[13px] font-semibold text-slate-800 font-['DM_Sans']">{opt.label}</div>
                <div className="text-[11px] text-slate-400 font-['DM_Sans'] mt-0.5">{opt.desc}</div>
              </div>
            </button>
          ))}
          <button onClick={() => onChoose("blank")} className="px-4 py-2.5 rounded-[10px] bg-transparent border border-dashed border-black/15 text-slate-400 text-xs cursor-pointer font-['DM_Sans']">Or just start writing...</button>
        </div>
      )}

      <button onClick={onClose} className="bg-none border-none text-slate-300 text-xs cursor-pointer font-['DM_Sans']">Cancel</button>
    </div>
  );
}

// ── Delete Confirm Modal ───────────────────────────────────────
function DeleteConfirmModal({ doc, onConfirm, onCancel }) {
  if (!doc) return null;
  return (
    <div className="absolute inset-0 bg-black/20 backdrop-blur-[2px] z-[120] flex items-center justify-center p-8" onMouseDown={onCancel}>
      <div className="bg-white border border-black/10 rounded-[14px] px-5 py-4 shadow-[0_8px_32px_rgba(0,0,0,0.18)] w-[320px] font-['DM_Sans']" onMouseDown={e => e.stopPropagation()}>
        <div className="text-[13px] font-semibold text-slate-800 mb-1.5">Delete this document?</div>
        <div className="text-xs text-slate-500 mb-3.5 leading-[1.5]">
          <em>{doc.title || "Untitled"}</em> will be permanently removed. This can't be undone.
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="px-3 py-2 rounded-lg bg-black/[0.04] border border-black/[0.08] text-slate-500 text-xs font-semibold cursor-pointer">Cancel</button>
          <button onClick={onConfirm} className="px-3 py-2 rounded-lg border-none text-white text-xs font-semibold cursor-pointer" style={{ background: "#dc2626" }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ── MAIN DESK PANEL ────────────────────────────────────────────
export default function DeskPanel({
  onClose,
  surfaceState,
  onSurfaceUpdate,
  onDeskDocumentChange,
  onDeleteDocument,
  focusDeskDoc,
  pendingDeskAction,
  onResolvePendingDeskAction,
  onUndoDeskReplace,
  registerDeskFlush,
  worldSaving,
  worldLastSavedAt,
  worldAutosavePending,
  worldSaveError,
  onAutosaveUiReset,
  roomCtx,
  roomId,
  focusDocTitle,
  onFocusHandled,
  existingDocTitles,
}) {
  const { saveToBookshelf, deleteDocument: deleteFromBookshelf } = useBookshelf();
  const [documents, setDocuments] = useState([]);
  const [activeDocId, setActiveDocId] = useState(null);
  const [focusMode, setFocusMode] = useState(false);
  const [sidecarOpen, setSidecarOpen] = useState(false);
  const [sidecarSession, setSidecarSession] = useState(null); // { sessionId, selectedText, thread, latestRevision }
  const [selectionPopover, setSelectionPopover] = useState(null); // { top, left }
  const [sidecarSessions, setSidecarSessions] = useState({}); // { [sessionId]: { selectedText, thread, latestRevision } }
  const [showCreativeStarts, setShowCreativeStarts] = useState(false);
  const [pendingTitle, setPendingTitle] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null); // doc pending delete confirmation
  const [organizing, setOrganizing] = useState(false); // Lumen organize/adjust request in flight
  const [organizedHtml, setOrganizedHtml] = useState(null); // Lumen's reshaped version shown in the lane, not yet committed
  const [adjustInput, setAdjustInput] = useState(""); // nudge note for reshaping
  const [timerActive, setTimerActive] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(25 * 60);
  const [timerDuration, setTimerDuration] = useState(25 * 60);
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);
  const processedDocIds = useRef(new Set());
  const handledFocusRef = useRef(null);
  const lastCountedSaveRef = useRef(null);
  // docId -> the content this panel last wrote out, so its own echo can be told apart from a
  // genuine external write (Lumen) when reconciling against surfaceState.
  const localEchoRef = useRef({});
  const editorContainerRef = useRef(null);
  const hydratingEditorRef = useRef(true);
  const pendingPushRef = useRef(null); // { docId, patch } not yet sent to SpatialContext
  const pushTimerRef = useRef(null);

  const toastTimerRef = useRef(null);
  // action: optional { label, onClick } shown as a button in the toast (kept up longer).
  const showToast = (msg, action = null) => {
    clearTimeout(toastTimerRef.current);
    setToast({ msg, action });
    toastTimerRef.current = setTimeout(() => setToast(null), action ? 6000 : 2500);
  };

  const CONTEXT_PUSH_MS = 350;

  const flushPendingPush = useCallback(() => {
    if (pushTimerRef.current != null) {
      clearTimeout(pushTimerRef.current);
      pushTimerRef.current = null;
    }
    const pending = pendingPushRef.current;
    pendingPushRef.current = null;
    if (pending) onDeskDocumentChange?.(pending.docId, pending.patch);
  }, [onDeskDocumentChange]);

  const queuePush = useCallback((docId, patch) => {
    if (pendingPushRef.current && pendingPushRef.current.docId !== docId) flushPendingPush();
    pendingPushRef.current = {
      docId,
      patch: { ...(pendingPushRef.current?.patch || {}), ...patch },
    };
    if (pushTimerRef.current == null) {
      pushTimerRef.current = setTimeout(() => {
        pushTimerRef.current = null;
        flushPendingPush();
      }, CONTEXT_PUSH_MS);
    }
  }, [flushPendingPush]);

  useEffect(() => () => flushPendingPush(), [flushPendingPush]);

  // Lumen writes flush this first, so a section it adds lands on top of what was just typed.
  useEffect(() => registerDeskFlush?.(flushPendingPush), [registerDeskFlush, flushPendingPush]);

  const closeAfterFlush = useCallback(() => {
    flushPendingPush();
    setTimeout(() => onClose?.(), 0);
  }, [flushPendingPush, onClose]);

  useEffect(() => {
    onAutosaveUiReset?.();
  }, [onAutosaveUiReset]);

  // Load documents from surfaceState - deduplicated
  useEffect(() => {
    if (!surfaceState?.deskDocuments?.length) return;
    const newlyAdded = [];
    surfaceState.deskDocuments.forEach(doc => {
      if (processedDocIds.current.has(doc.id)) return;
      if (doc.sourceSurfaceItemId && processedDocIds.current.has(doc.sourceSurfaceItemId)) return;
      processedDocIds.current.add(doc.id);
      if (doc.sourceSurfaceItemId) processedDocIds.current.add(doc.sourceSurfaceItemId);

      // Extract title from doc or from H1 in content
      const titleFromH1 = extractTitle(doc.content);
      const title = doc.title || titleFromH1 || "Untitled";

      // Strip leading H1 from content to prevent duplicate heading
      const cleanContent = stripLeadingH1(doc.content, title);

      newlyAdded.push({ ...doc, title, content: cleanContent, version: 1, timestamp: Date.now() });
    });
    if (!newlyAdded.length) return;
    setDocuments(prev => [...prev, ...newlyAdded]);

    // DeskPanel fully remounts every time it reopens, so this hydration runs from
    // scratch each time — normally the most recently surfaced doc wins focus. But
    // if the caller asked to open one specific doc by title (e.g. "Open in Desk"
    // from the Bookshelf), that one wins instead of whatever happens to be last in
    // a list that can span every doc created this session.
    const wanted = focusDocTitle?.trim().toLowerCase();
    const focusTarget = wanted
      ? newlyAdded.find(d => (d.title || "").trim().toLowerCase() === wanted)
      : null;
    setActiveDocId((focusTarget || newlyAdded[newlyAdded.length - 1]).id);
  }, [surfaceState?.deskDocuments?.length, focusDocTitle]);

  // The focus request above is one-shot — tell the parent to clear it so it can't
  // also hijack focus for some unrelated doc created later in this same session.
  useEffect(() => {
    if (focusDocTitle) onFocusHandled?.();
  }, [focusDocTitle, onFocusHandled]);

  useEffect(() => {
    if (!surfaceState?.deskDocuments?.length) return;
    setDocuments(prev => {
      let changed = false;
      const next = prev.map(d => {
        const latest = surfaceState.deskDocuments.find(sd => sd.id === d.id || sd.sourceSurfaceItemId === d.id);
        if (!latest) return d;
        if (localEchoRef.current[d.id] === (latest.content || "")) return d;

        const title = latest.title || d.title;
        const cleanContent = stripLeadingH1(latest.content, title);
        if (cleanContent === d.content && title === d.title) return d;
        changed = true;
        return { ...d, content: cleanContent, title, timestamp: Date.now() };
      });
      return changed ? next : prev;
    });
  }, [surfaceState?.deskDocuments]);

  useEffect(() => {
    const docId = focusDeskDoc?.docId;
    if (!docId) return;

    const requestKey = `${docId}:${focusDeskDoc.at || ""}`;
    if (handledFocusRef.current === requestKey) return;
    const exists = (surfaceState?.deskDocuments || []).some(
      d => d.id === docId || d.sourceSurfaceItemId === docId
    );
    if (!exists) return;
    handledFocusRef.current = requestKey;
    setActiveDocId(docId);
  }, [focusDeskDoc, surfaceState?.deskDocuments]);

  const activeDoc = documents.find(d => d.id === activeDocId);

  const editor = useEditor({
    shouldRerenderOnTransaction: true,
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: "Start writing..." }),
      SidecarRevisionMark,
    ],
    content: activeDoc?.content || "",
    onUpdate: ({ editor }) => {
      if (!activeDocId || hydratingEditorRef.current) return;
      const content = editor.getHTML();
      localEchoRef.current[activeDocId] = content;
      setDocuments(prev => prev.map(d =>
        d.id === activeDocId
          ? { ...d, content, timestamp: Date.now() }
          : d
      ));
      queuePush(activeDocId, { content, userEdit: true });
    },
    editorProps: {
      attributes: { style: "outline: none; min-height: 400px; padding: 24px 32px; font-family: 'DM Sans', sans-serif; font-size: 14px; line-height: 1.9; color: #334155;" },
      handleDOMEvents: {
        copy: (view, event) => copyInlineSelection(view, event),
        cut: (view, event) => copyInlineSelection(view, event, true),
      },
      handleClick(view, pos, event) {
        // Check if clicked on a sidecarRevision mark
        const node = event.target;
        const sessionId = node?.dataset?.sidecarSession || node?.closest?.("[data-sidecar-session]")?.dataset?.sidecarSession;
        if (sessionId) {
          const existing = sidecarSessions[sessionId];
          if (existing) {
            setSidecarSession({ sessionId, ...existing });
            setSidecarOpen(true);
            setSelectionPopover(null);
            return true;
          }
        }
        return false;
      },
    },
  });

  useEffect(() => {
    if (!editor || !activeDocId) return;
    const content = activeDoc?.content || "";
    const current = editor.getHTML();
    if (current === content) {
      hydratingEditorRef.current = false;
      return;
    }
    hydratingEditorRef.current = true;
    setOrganizedHtml(null); setAdjustInput(""); // Lumen lane shouldn't carry across documents
    editor.commands.setContent(content, false);
    queueMicrotask(() => {
      hydratingEditorRef.current = false;
    });
  }, [activeDocId, editor, activeDoc?.content]);

  useEffect(() => {
    if (!worldLastSavedAt || !activeDocId) return;
    if (lastCountedSaveRef.current === null) {
      lastCountedSaveRef.current = worldLastSavedAt;
      return;
    }
    if (lastCountedSaveRef.current === worldLastSavedAt) return;
    lastCountedSaveRef.current = worldLastSavedAt;
    setDocuments(prev => prev.map(d =>
      d.id === activeDocId ? { ...d, version: (d.version || 1) + 1 } : d
    ));
  }, [worldLastSavedAt, activeDocId]);

  useEffect(() => {
    if (timerActive && timerSeconds > 0) {
      timerRef.current = setInterval(() => setTimerSeconds(s => s - 1), 1000);
    } else if (timerSeconds === 0) {
      setTimerActive(false);
      showToast("Time's up. Take a break?");
    }
    return () => clearInterval(timerRef.current);
  }, [timerActive, timerSeconds]);

  useEffect(() => {
    if (!focusMode) return;
    const onKeyDown = (e) => { if (e.key === "Escape") setFocusMode(false); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [focusMode]);

  const formatTime = s => `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;
  const endTime = () => new Date(Date.now() + timerSeconds * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const newDocument = (title = "") => { setPendingTitle(title); setShowCreativeStarts(true); };

  // onChoose now receives the scaffold HTML string directly (from Lumen-generated options
  // or from the fallback), or the special string "blank" for an empty document.
  const handleCreativeStart = (scaffoldOrBlank) => {
    setShowCreativeStarts(false);
    const content = scaffoldOrBlank === "blank" ? "" : (scaffoldOrBlank || "");
    const id = `doc-${Date.now()}`;
    const title = pendingTitle || "Untitled";
    onSurfaceUpdate?.({ surface: "desk", id, title, content });
    processedDocIds.current.add(id);
    const doc = { id, title, content, version: 1, timestamp: Date.now() };
    setDocuments(prev => [...prev, doc]);
    setActiveDocId(id);
    setTimeout(() => editor?.commands.focus("end"), 100);
  };

  // ── Organize a brain dump via Lumen ──────────────────────────
  // Replaces the raw dump in place with a structured version and keeps the
  // original around so a single Undo restores it. No preview, no extra choices.
  const writeDocContent = (html) => {
    if (!editor || !activeDocId) return;
    editor.commands.setContent(html);
    localEchoRef.current[activeDocId] = html;
    setDocuments(prev => prev.map(d =>
      d.id === activeDocId ? { ...d, content: html, timestamp: Date.now() } : d
    ));
    flushPendingPush();
    onDeskDocumentChange?.(activeDocId, { content: html, userEdit: true });
  };

  const ORGANIZE_SYSTEM = "You are Lumen, organizing a raw brain dump into a clean structure. Group the user's own thoughts into logical sections, each with an <h2> header, followed by short paragraphs or <ul> bullet lists. Preserve every idea the user wrote. Do NOT add new ideas, opinions, advice, or content of your own. Do NOT include the original prompt/instruction line. Respond with ONLY the organized HTML — no preamble, no explanation, no markdown code fences.";

  // Zone 2: ask Lumen to shape the dump. Result shows in the Lumen lane; the
  // raw dump stays untouched in the entry above until the user keeps it.
  const organizeDump = async () => {
    if (!activeDoc || organizing) return;
    const raw = editor?.getHTML() || activeDoc.content || "";
    const plain = raw.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (plain.length < 15) { showToast("Dump a bit more first, then organize."); return; }
    setOrganizing(true);
    try {
      const result = await ClaudeService.sendMessage({
        model: "claude-sonnet-4-6",
        maxTokens: 1500,
        system: ORGANIZE_SYSTEM,
        messages: [{ role: "user", content: plain }],
      });
      if (!result.success) throw new Error(result.error);
      const html = (result.content || "").replace(/```html|```/g, "").trim();
      if (!html) throw new Error("Empty response");
      setOrganizedHtml(html);
    } catch (e) {
      console.error("Organize error:", e);
      showToast("Couldn't organize that. Try again.");
    } finally {
      setOrganizing(false);
    }
  };

  // Nudge Lumen to reshape the current organized version.
  const adjustOrganized = async () => {
    if (!organizedHtml || organizing) return;
    const note = adjustInput.trim();
    if (!note) return;
    setOrganizing(true);
    try {
      const result = await ClaudeService.sendMessage({
        model: "claude-sonnet-4-6",
        maxTokens: 1500,
        system: ORGANIZE_SYSTEM,
        messages: [{ role: "user", content: `Here is the current organized version:\n\n${organizedHtml}\n\nRevise it based on this note, keeping every original idea: "${note}"` }],
      });
      if (!result.success) throw new Error(result.error);
      const html = (result.content || "").replace(/```html|```/g, "").trim();
      if (!html) throw new Error("Empty response");
      setOrganizedHtml(html);
      setAdjustInput("");
    } catch (e) {
      console.error("Adjust error:", e);
      showToast("Couldn't adjust that. Try again.");
    } finally {
      setOrganizing(false);
    }
  };

  // Zone 3: commit the organized version into the document.
  const keepOrganized = () => {
    if (!organizedHtml) return;
    writeDocContent(organizedHtml);
    setOrganizedHtml(null);
    setAdjustInput("");
    showToast("Kept. Your dump is now organized.");
  };

  const discardOrganized = () => {
    setOrganizedHtml(null);
    setAdjustInput("");
  };

  const confirmDelete = (docId) => {
    const wasActive = activeDocId === docId;
    const remaining = documents.filter(d => d.id !== docId);
    setDocuments(remaining);
    if (wasActive) setActiveDocId(remaining[0]?.id || null);
    onDeleteDocument?.(docId);
    deleteFromBookshelf(bookIdForDeskDoc(docId));
    setDeleteTarget(null);
    showToast("Document deleted.");
  };

  const updateTitle = (title) => {
    setDocuments((prev) =>
      prev.map((d) => (d.id === activeDocId ? { ...d, title, timestamp: Date.now() } : d))
    );
    // Typed one character at a time, same as the body — batch it the same way.
    if (activeDocId) queuePush(activeDocId, { title, userEdit: true });
  };

  const handleSelection = useCallback(() => {
    const sel = window.getSelection();
    const text = sel?.toString().trim();
    if (!text || text.length < 10) {
      setSelectionPopover(null);
      return;
    }
    // Position popover above the selection
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    setSelectionPopover({
      top: rect.top + window.scrollY,
      left: rect.left + rect.width / 2 + window.scrollX,
      text,
    });
  }, []);

  const openSidecarFromSelection = () => {
    if (!selectionPopover?.text || !editor) return;
    const sessionId = `sidecar-${Date.now()}`;
    // Capture TipTap selection positions NOW before focus moves to sidecar
    const { from, to } = editor.state.selection;
    setSidecarSession({ sessionId, selectedText: selectionPopover.text, thread: null, latestRevision: null, from, to });
    setSidecarOpen(true);
    setSelectionPopover(null);
  };

  const commitToMaster = (revisedHTML, finalThread) => {
    if (!editor || !sidecarSession) return;
    const { sessionId, selectedText } = sidecarSession;

    // Re-commits (refining an already-committed revision) must replace the mark's *live*
    // range, not the range captured when the panel first opened - the doc has since
    // changed shape (e.g. inline text became a <ul>), so old positions no longer line up.
    // Only the very first commit for a session has no mark yet and falls back to the
    // original text selection.
    const liveRange = findSidecarMarkRange(editor.state.doc, sessionId);
    const { from, to } = liveRange || sidecarSession;

    // Insert as parsed HTML (not a flat text node) so block structure like
    // <ul>/<ol>/<li> and headings survive instead of collapsing to plain text.
    let insertedTo = to;
    editor.chain()
      .focus()
      .insertContentAt({ from, to }, revisedHTML)
      .command(({ tr }) => {
        // Map the old end position through this transaction to find where the
        // inserted content actually ends now - selection-based heuristics can land
        // mid-node for block content and silently corrupt the document instead.
        insertedTo = tr.mapping.map(to);
        return true;
      })
      .run();

    // Mark the full inserted range as a sidecar revision so it can be reopened later.
    editor.chain()
      .setTextSelection({ from, to: insertedTo })
      .setMark("sidecarRevision", { sessionId })
      .run();

    setSidecarSessions(prev => ({
      ...prev,
      [sessionId]: { selectedText, thread: finalThread, latestRevision: revisedHTML },
    }));

    setSidecarOpen(false);
    setSidecarSession(null);
    showToast("Committed. Click the underlined text to revisit.");
  };

  const handleSidecarClose = (finalThread, finalLatest) => {
    if (sidecarSession && finalThread) {
      setSidecarSessions(prev => ({
        ...prev,
        [sidecarSession.sessionId]: {
          selectedText: sidecarSession.selectedText,
          thread: finalThread,
          latestRevision: finalLatest,
        },
      }));
    }
    setSidecarOpen(false);
    setSidecarSession(null);
  };

  const sendToBookshelf = () => {
    if (!activeDoc) return;
    const doc = activeDoc;
    saveToBookshelf({
      id: bookIdForDeskDoc(doc.id),
      title: doc.title || "Untitled",
      type: "document",
      content: doc.content || "",
      sourceHotspot: "desk",
      roomId: roomId || roomCtx?.room?.id || null,
    });

    if (pendingPushRef.current?.docId === doc.id) pendingPushRef.current = null;

    const remaining = documents.filter((d) => d.id !== doc.id);
    setDocuments(remaining);
    setActiveDocId(remaining[0]?.id || null);
    onDeleteDocument?.(doc.id);
    showToast("Sent to Bookshelf.");
  };

  // Human in the loop handlers
  // Both choices go through the same desk write as Lumen's, so they are saved and confirmed back
  // to Lumen instead of only changing this panel.
  const resolvePending = async (choice) => {
    if (!pendingDeskAction?.deskUpdate) return;
    const receipt = await onResolvePendingDeskAction?.(choice);
    if (!receipt) return;
    if (receipt.keptBoth) {
      showToast(`Kept both. Opened “${receipt.title}”. Your original is still in Documents.`);
    } else if (receipt.status === "replaced" && onUndoDeskReplace) {
      showToast(describeDeskReceipt(receipt), {
        label: "Undo",
        onClick: async () => {
          const undone = await onUndoDeskReplace();
          showToast(undone?.ok ? describeDeskReceipt(undone.receipt) : "Can't undo. The doc has changed since.");
        },
      });
    } else {
      showToast(describeDeskReceipt(receipt));
    }
  };
  const handleDecisionNewDoc = () => resolvePending("new");
  const handleDecisionAppend = () => resolvePending("append");
  const handleDecisionReplace = () => resolvePending("replace");

  const countWords = (html) => String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&(?:quot|#34);/gi, '"')
    .replace(/&(?:apos|#39);/gi, "'")
    .split(/\s+/)
    .filter(Boolean).length;

  const wordCount = editor?.getText()?.split(/\s+/).filter(Boolean).length || 0;

  if (focusMode && editor) {
    return (
      <div className="fixed inset-0 z-[200] bg-[#F8F6F1] flex flex-col items-center px-6 py-10 font-['DM_Sans']"
        onKeyDown={e => { if (e.key === "Escape") setFocusMode(false); }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
        <div className="w-full max-w-[680px] flex flex-col flex-1 min-h-0">
          <div className="flex justify-between items-center mb-6">
            <div className="text-[11px] text-slate-400">Focus Mode · Press Esc to exit</div>
            <button onClick={() => setFocusMode(false)} className="px-4 py-[7px] rounded-lg bg-white border border-black/[0.12] text-slate-800 text-xs font-semibold cursor-pointer shadow-[0_1px_4px_rgba(0,0,0,0.08)]">← Exit Focus</button>
          </div>
          <input value={activeDoc?.title || ""} onChange={e => updateTitle(e.target.value)} className="text-[22px] font-bold text-slate-800 border-none outline-none bg-transparent mb-4 font-['DM_Sans']" />
          <div className="flex-1 min-h-0 overflow-auto"><EditorContent editor={editor} /></div>
          <div className="text-[11px] text-slate-300 pt-3 text-right">{wordCount} words</div>
        </div>
        <style>{tiptapStyles}</style>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full font-['DM_Sans'] bg-slate-50 relative">
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-black/[0.06] bg-white flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-base">🗒</span>
          <span className="text-sm font-bold text-slate-800">Desk</span>
          {activeDoc && <span className="text-[11px] text-slate-400">· {activeDoc.title} · {wordCount} words</span>}
        </div>
        <div className="flex gap-1.5 items-center">
          {activeDoc && (
            <>
              <button onClick={() => setFocusMode(true)} className="px-3 py-[5px] rounded-lg bg-black/[0.04] border border-black/[0.08] text-slate-600 text-xs font-medium font-['DM_Sans'] cursor-pointer transition-all duration-150">Focus</button>
              <button onClick={sendToBookshelf} className="px-3 py-[5px] rounded-lg bg-black/[0.04] border border-blue-500/30 text-blue-500 text-xs font-medium font-['DM_Sans'] cursor-pointer transition-all duration-150">📚 Send to Bookshelf</button>
            </>
          )}
          <button onClick={() => newDocument()} className="px-3 py-[5px] rounded-lg bg-black/[0.04] border border-accent-500/30 text-xs font-medium font-['DM_Sans'] cursor-pointer transition-all duration-150" style={{ color: TEAL }}>+ New</button>
          <button onClick={closeAfterFlush} className="px-3 py-[5px] rounded-lg bg-black/[0.04] border border-black/[0.08] text-slate-400 text-xs font-medium font-['DM_Sans'] cursor-pointer transition-all duration-150">✕</button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden">
        {documents.length >= 1 && (
          <div className="w-[180px] flex-shrink-0 border-r border-black/[0.06] px-2 py-3 flex flex-col gap-1 overflow-y-auto bg-white">
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.05em] px-2 mb-1">Documents</div>
            {documents.map(doc => (
              <div key={doc.id} className="group relative flex items-center rounded-lg" style={{ background: activeDocId === doc.id ? "rgba(77,184,181,0.08)" : "transparent" }}>
                <button onClick={() => setActiveDocId(doc.id)} onContextMenu={e => { e.preventDefault(); setDeleteTarget(doc); }} className="flex-1 min-w-0 px-2.5 py-2 rounded-lg border-none text-left cursor-pointer bg-transparent">
                  <div className="text-xs font-semibold font-['DM_Sans'] truncate" style={{ color: activeDocId === doc.id ? TEAL : "#334155" }}>{doc.title}</div>
                  <div className="text-[10px] text-slate-400 font-['DM_Sans'] mt-0.5">{doc.id === activeDocId ? wordCount : countWords(doc.content)} words</div>
                </button>
                <button onClick={e => { e.stopPropagation(); setDeleteTarget(doc); }} title="Delete document"
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex-shrink-0 px-2 py-2 mr-0.5 bg-transparent border-none cursor-pointer text-slate-400 hover:text-red-500 text-xs">🗑</button>
              </div>
            ))}
          </div>
        )}

        <div className="flex-1 flex flex-col overflow-hidden">
          {!activeDoc ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8">
              <span className="text-[40px] opacity-20">🗒</span>
              <div className="text-sm text-slate-400 font-['DM_Sans']">Nothing on the desk yet.</div>
              <div className="text-xs text-slate-300 font-['DM_Sans'] text-center max-w-[260px]">Ask Lumen to draft something, or start a new document.</div>
              <button onClick={() => newDocument()} className="px-5 py-2 rounded-[10px] border-none text-white text-[13px] font-semibold cursor-pointer font-['DM_Sans']" style={{ background: TEAL }}>+ New Document</button>
            </div>
          ) : (
            <>
              <div className="px-5 py-2 border-b border-black/[0.05] bg-accent-500/[0.04] flex items-center gap-4 flex-shrink-0">
                <span className="text-[11px] font-['DM_Sans']" style={{ color: worldSaveError ? "#dc2626" : "#94a3b8" }}>
                  {worldSaveError
                    ? "Save failed — try closing the desk to retry"
                    : worldSaving
                      ? "Saving…"
                      : worldAutosavePending
                        ? "Autosaving…"
                        : worldLastSavedAt
                          ? `✓ Saved ${new Date(worldLastSavedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
                          : null}
                </span>
                <span className="text-[11px] text-slate-300 font-['DM_Sans']">v{activeDoc.version}</span>
              </div>
              <FormattingToolbar editor={editor} />
              <div className="px-8 pt-4 flex-shrink-0">
                <input value={activeDoc.title} onChange={e => updateTitle(e.target.value)} placeholder="Untitled"
                  className="block w-full text-[22px] font-bold text-slate-800 border-none outline-none bg-transparent font-['DM_Sans'] box-border" />
              </div>
              <div className="flex-1 overflow-auto relative" onMouseUp={handleSelection} onKeyUp={handleSelection} ref={editorContainerRef}>
                <EditorContent editor={editor} />
                <SelectionPopover position={selectionPopover} onOpen={openSidecarFromSelection} />
              </div>
              {activeDoc.startType === "braindump" && (
                <div className="flex-shrink-0 border-t border-black/[0.06] font-['DM_Sans']" style={{ background: "rgba(77,184,181,0.06)" }}>
                  {/* Zone 2 — Lumen lane */}
                  <div className="px-5 pt-2.5 pb-2">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <span style={{ color: TEAL }}>✦</span>
                      <span className="text-[10px] font-bold uppercase tracking-[0.06em]" style={{ color: "#0F6E56" }}>Lumen</span>
                    </div>
                    {organizedHtml ? (
                      <>
                        <div className="organize-lane max-h-[170px] overflow-auto rounded-lg bg-white border border-black/[0.06] px-3.5 py-2.5 text-[13px] text-slate-700 leading-[1.6]"
                          dangerouslySetInnerHTML={{ __html: organizedHtml }} />
                        <div className="flex gap-1.5 mt-2">
                          <input value={adjustInput} onChange={e => setAdjustInput(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && adjustOrganized()}
                            placeholder="Tighter, or group by theme..." disabled={organizing}
                            className="flex-1 px-2.5 py-2 rounded-lg border border-black/10 bg-white text-slate-800 text-[12px] font-['DM_Sans'] outline-none" />
                          <button onClick={adjustOrganized} disabled={organizing || !adjustInput.trim()}
                            className="px-3 py-2 rounded-lg bg-white border border-black/[0.12] text-slate-600 text-[12px] font-semibold font-['DM_Sans'] flex-shrink-0"
                            style={{ cursor: organizing || !adjustInput.trim() ? "default" : "pointer", opacity: organizing || !adjustInput.trim() ? 0.5 : 1 }}>
                            {organizing ? "…" : "Adjust"}
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="text-[12px] text-slate-500 leading-[1.5]">
                        {organizing ? "Shaping your dump into sections…" : "Sections appear here after you organize. Nudge me if the grouping's off."}
                      </div>
                    )}
                  </div>
                  {/* Zone 3 — actions */}
                  <div className="px-5 pb-2.5 pt-0.5 flex items-center gap-2 justify-end">
                    {organizedHtml ? (
                      <>
                        <button onClick={discardOrganized} disabled={organizing}
                          className="px-3 py-[6px] rounded-lg bg-white border border-black/[0.12] text-slate-500 text-[12px] font-semibold font-['DM_Sans'] cursor-pointer">Discard</button>
                        <button onClick={keepOrganized} disabled={organizing}
                          className="px-3.5 py-[6px] rounded-lg border-none text-white text-[12px] font-semibold font-['DM_Sans']"
                          style={{ background: TEAL, cursor: "pointer" }}>Keep organized</button>
                      </>
                    ) : (
                      <button onClick={organizeDump} disabled={organizing}
                        className="px-3.5 py-[6px] rounded-lg border-none text-white text-[12px] font-semibold font-['DM_Sans']"
                        style={{ background: organizing ? "rgba(77,184,181,0.4)" : TEAL, cursor: organizing ? "default" : "pointer" }}>
                        {organizing ? "Organizing…" : "Organize"}
                      </button>
                    )}
                  </div>
                </div>
              )}
              <div className="px-5 py-2.5 border-t border-black/[0.05] flex items-center justify-between bg-white flex-shrink-0 gap-1.5">
                <span className="text-[11px] text-slate-300 font-['DM_Sans'] flex-shrink-0">{wordCount} words</span>
                <div className="flex gap-[5px] flex-wrap justify-end">
                  {/* Send to the Bookshelf - takes the doc off the Desk */}
                  <button onClick={sendToBookshelf} className="px-3 py-[5px] rounded-lg bg-black/[0.04] border border-blue-500/30 text-blue-500 text-[11px] font-medium font-['DM_Sans'] cursor-pointer transition-all duration-150">📚 Send to Bookshelf</button>
                  {/* Download */}
                  <button onClick={() => {
                    const blob = new Blob([`${activeDoc.title}\n\n${activeDoc.content?.replace(/<[^>]+>/g, "")}`], { type: "text/plain" });
                    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
                    a.download = `${activeDoc.title || "document"}.txt`; a.click();
                  }} className="px-3 py-[5px] rounded-lg bg-black/[0.04] border border-green-500/30 text-green-500 text-[11px] font-medium font-['DM_Sans'] cursor-pointer transition-all duration-150">↓ Download</button>
                  {/* Email */}
                  <button onClick={() => {
                    const body = encodeURIComponent(activeDoc.content?.replace(/<[^>]+>/g, "\n") || "");
                    const subject = encodeURIComponent(activeDoc.title || "Document");
                    window.open(`mailto:?subject=${subject}&body=${body}`);
                  }} className="px-3 py-[5px] rounded-lg bg-black/[0.04] border border-purple-500/30 text-purple-500 text-[11px] font-medium font-['DM_Sans'] cursor-pointer transition-all duration-150">✉ Email</button>
                  {/* Google Drive - Phase 2 */}
                  <button onClick={() => showToast("Google Drive sync coming soon.")} className="px-3 py-[5px] rounded-lg bg-black/[0.04] border border-black/[0.08] text-slate-400 text-[11px] font-medium font-['DM_Sans'] cursor-pointer transition-all duration-150 opacity-60">⬆ Drive</button>
                  {/* Close only - the doc is already autosaved to the Desk */}
                  <button onClick={closeAfterFlush} className="px-3 py-[5px] rounded-lg bg-black/[0.04] border border-black/[0.12] text-slate-500 text-[11px] font-medium font-['DM_Sans'] cursor-pointer transition-all duration-150">✕ Close</button>
                </div>
              </div>
            </>
          )}
        </div>

        {sidecarOpen && sidecarSession && (
          <SidecarPanel
            sessionId={sidecarSession.sessionId}
            selectedText={sidecarSession.selectedText}
            initialThread={sidecarSession.thread}
            initialLatest={sidecarSession.latestRevision}
            onCommit={commitToMaster}
            onClose={handleSidecarClose}
          />
        )}
      </div>

      {pendingDeskAction && (
        <DocDecisionModal
          pendingAction={pendingDeskAction}
          onNewDoc={handleDecisionNewDoc}
          onAppend={handleDecisionAppend}
          onReplace={handleDecisionReplace}
          onDismiss={() => onResolvePendingDeskAction?.("dismiss")}
        />
      )}

      {showCreativeStarts && <CreativeStartsModal title={pendingTitle} roomCtx={roomCtx} existingDocTitles={existingDocTitles} onChoose={handleCreativeStart} onClose={() => setShowCreativeStarts(false)} />}

      {deleteTarget && <DeleteConfirmModal doc={deleteTarget} onConfirm={() => confirmDelete(deleteTarget.id)} onCancel={() => setDeleteTarget(null)} />}

      {toast && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 px-4 py-2 rounded-[10px] text-white text-xs font-semibold font-['DM_Sans'] shadow-[0_4px_16px_rgba(0,0,0,0.15)] z-[100]" style={{ background: TEAL }}>
          {toast.msg}
          {toast.action && (
            <button
              onClick={() => { const { onClick } = toast.action; setToast(null); onClick(); }}
              className="ml-3 px-2 py-0.5 rounded-md bg-white/20 border border-white/40 text-white text-xs font-semibold cursor-pointer"
            >
              {toast.action.label}
            </button>
          )}
        </div>
      )}

      <style>{tiptapStyles}</style>
    </div>
  );
}

const tiptapStyles = `
  .ProseMirror { outline: none; }
  .ProseMirror p { margin: 0 0 12px; }
  .ProseMirror h1 { font-size: 24px; font-weight: 700; color: #1E293B; margin: 24px 0 12px; line-height: 1.3; }
  .ProseMirror h2 { font-size: 18px; font-weight: 700; color: #1E293B; margin: 20px 0 10px; line-height: 1.4; }
  .ProseMirror h3 { font-size: 15px; font-weight: 700; color: #334155; margin: 16px 0 8px; }
  .ProseMirror ul { list-style: disc; padding-left: 24px; margin: 0 0 12px; }
  .ProseMirror ol { list-style: decimal; padding-left: 24px; margin: 0 0 12px; }
  .ProseMirror li { margin-bottom: 4px; }
  .ProseMirror blockquote { border-left: 3px solid #4DB8B5; margin: 0 0 12px; padding: 8px 16px; color: #64748B; font-style: italic; }
  .ProseMirror hr { border: none; border-top: 1px solid rgba(0,0,0,0.1); margin: 20px 0; }
  .ProseMirror strong { font-weight: 700; color: #1E293B; }
  .ProseMirror em { font-style: italic; }
  .ProseMirror s { text-decoration: line-through; color: #94A3B8; }
  .ProseMirror p.is-editor-empty:first-child::before { content: attr(data-placeholder); color: #CBD5E1; pointer-events: none; float: left; height: 0; }
  .ProseMirror [data-sidecar-session] { border-bottom: 2px solid #4DB8B5; cursor: pointer; position: relative; }
  .ProseMirror [data-sidecar-session]:hover { background: rgba(77,184,181,0.08); }
  .ProseMirror [data-sidecar-session]::after { content: "✦ Revised in Sidecar - click to refine"; position: absolute; bottom: calc(100% + 6px); left: 50%; transform: translateX(-50%); background: #1E293B; color: white; font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: 6px; white-space: nowrap; pointer-events: none; opacity: 0; transition: opacity 0.15s; font-family: 'DM Sans', sans-serif; }
  .ProseMirror [data-sidecar-session]:hover::after { opacity: 1; }
  .organize-lane h2 { font-size: 14px; font-weight: 700; color: #1E293B; margin: 12px 0 5px; }
  .organize-lane h2:first-child { margin-top: 0; }
  .organize-lane p { margin: 0 0 8px; }
  .organize-lane ul { list-style: disc; padding-left: 18px; margin: 0 0 8px; }
  .organize-lane li { margin-bottom: 2px; }
`;
